import { FolderPlusIcon, PlusIcon } from "@phosphor-icons/react";
import type React from "react";
import { useEffect, useRef, useState } from "react";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTriggerWithActions,
} from "@/components/ui/accordion";
import { Button, ShowMoreButton } from "@/components/ui/button";
import { ItemSkeleton } from "@/components/ui/feedback/skeleton";
import IconButton from "@/components/ui/icon-button";
import { Menu, MenuItem, MenuPopover, MenuTrigger } from "@/components/ui/menu";
import SearchBar from "@/components/ui/molecules/FilterSearchBar";
import { useKeyMessageSubscriptions } from "@/data-access/news-feeds/subscriptions/useKeyMessageSubcriptions";
import useNewsFeedFilters from "@/store/news-feed/filters/newsFeed-filter.slice";
import { useGetKeyMessageFilterItems } from "@/store/news-feed/key-messages/key-message.selectors";
import useKeyMessageStore from "@/store/news-feed/key-messages/key-message.slice";
import {
  fetchKeyMessagesAndGroups,
  setAllKeyMessageCountsLoading,
} from "@/store/news-feed/key-messages/key-message-async.actions";
import { useAnalysisSidebarFilterParams } from "@/store/news-feed/params/analysis-params.context";
import { useAnalysisParamsForQueries } from "@/store/news-feed/params/useAnalysisParamsForQueries";
import KeyMessageGroupItem from "./KeyMessageGroupItem";

import { useFilterVisibility } from "./useFilterVisibility";
import {
  useKeyMessageExpandablePagination,
  useKeyMessageFilterState,
} from "./useKeyMessageHelpers";

const KeyMessageFilterContainer = () => {
  const {
    feedId,
    startDate,
    endDate,
    selectedFilters: analysisSelectedFilters,
  } = useAnalysisParamsForQueries();

  // Get key messages data from store
  const keyMessages = useKeyMessageStore((state) => state.keyMessages);
  const keyMessageGroups = useKeyMessageStore(
    (state) => state.keyMessageGroups,
  );
  const isFetchingKeyMessageGroups = useKeyMessageStore(
    (state) => state.isFetchingKeyMessagesAndGroups,
  );

  // Get expanded state from news feed filters store
  const expandedCategories = useNewsFeedFilters(
    (state) => state.expandedCategories,
  );
  const setCategoryExpansion = useNewsFeedFilters(
    (state) => state.setCategoryExpansion,
  );
  const isExpanded = expandedCategories.has("KEY_MESSAGE");

  // Get key message actions from store
  const setCreateKeyMessageModalOpen = useKeyMessageStore(
    (state) => state.setCreateKeyMessageModalOpen,
  );
  const setCreateKeyMessageGroupModalOpen = useKeyMessageStore(
    (state) => state.setCreateKeyMessageGroupModalOpen,
  );
  const setSelectedGroup = useKeyMessageStore(
    (state) => state.setSelectedGroup,
  );
  const setSelectedGroups = useKeyMessageStore(
    (state) => state.setSelectedGroups,
  );

  // Search state
  const [searchTerm, setSearchTerm] = useState("");

  // Selected items from sidebar filter context (works on both newsfeeds and Analysis)
  const { selectedFilters } = useAnalysisSidebarFilterParams();
  const selectedKeyMessageIds = new Set(
    selectedFilters.get("KEY_MESSAGE") || [],
  );
  const selectedGroups = useKeyMessageStore((state) => state.selectedGroups);
  const selectedGroup = useKeyMessageStore((state) => state.selectedGroup);

  // Use the key message filter state hook
  const {
    toggleGroupSelection,
    toggleItemSelection,
    filteredGroups,
    toggleExpandGroups,
    expandedGroups,
  } = useKeyMessageFilterState(searchTerm, selectedGroups);

  const filteredKeyMessages = useGetKeyMessageFilterItems(searchTerm);

  // Use pagination hook for filtered groups
  const {
    visibleCount,
    showMore: toggleShowMore,
    showLess: toggleShowLess,
    hasMoreToShow,
    canShowLess,
  } = useKeyMessageExpandablePagination(filteredGroups, searchTerm);

  // Search handlers
  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(event.target.value);
  };

  const handleClearSearch = () => {
    setSearchTerm("");
  };

  // Visibility
  const { showSearch: shouldShowSearch } = useFilterVisibility(
    filteredKeyMessages.length,
    keyMessages.length,
    !!searchTerm,
  );

  // Subscribe to key message updates
  useKeyMessageSubscriptions();

  // When subscription filters change, counts are recalculated — show spinner until updates arrive.
  // Skip when only feedId changed (fetchKeyMessagesAndGroups handles that and sets loading on new data).
  const subscriptionFiltersKeyRef = useRef<string | null>(null);
  const previousFeedIdRef = useRef<string | undefined>(undefined);
  useEffect(() => {
    if (!feedId || !startDate || !endDate) return;
    const key = JSON.stringify({
      feedId,
      startDate,
      endDate,
      selectedFilters: analysisSelectedFilters,
    });
    const filtersKeyChanged =
      subscriptionFiltersKeyRef.current !== null &&
      subscriptionFiltersKeyRef.current !== key;
    const feedIdChanged = previousFeedIdRef.current !== feedId;
    previousFeedIdRef.current = feedId;
    subscriptionFiltersKeyRef.current = key;
    if (filtersKeyChanged && !feedIdChanged) {
      setAllKeyMessageCountsLoading(true);
    }
  }, [feedId, startDate, endDate, analysisSelectedFilters]);

  // Fetch key messages and groups when feed or date range changes
  useEffect(() => {
    if (feedId) {
      fetchKeyMessagesAndGroups({ feedId });
    }
  }, [feedId]);

  // Clear selected key messages when feedId changes
  useEffect(() => {
    setSelectedGroups(new Set());
  }, [feedId, setSelectedGroups]);

  // Keep group expanded when a new key message is created
  useEffect(() => {
    if (selectedGroup && !expandedGroups.has(selectedGroup.id)) {
      toggleExpandGroups(selectedGroup.id, true);
    }
  }, [selectedGroup, expandedGroups, toggleExpandGroups]);

  return (
    <Accordion
      type="single"
      collapsible
      value={isExpanded ? "KEY_MESSAGE" : ""}
      onValueChange={(value) => {
        setCategoryExpansion("KEY_MESSAGE", value === "KEY_MESSAGE");
      }}
    >
      <AccordionItem value="KEY_MESSAGE" className="px-2">
        <AccordionTriggerWithActions
          size="sm"
          className="font-medium text-xs text-gray-700 py-2"
          actions={
            <MenuTrigger>
              <IconButton
                icon={<PlusIcon />}
                aria-label="Add Key Topic or group"
                variant="ghost"
                size="sm"
                tooltipLabel="Add Key Topic or group"
              />
              <MenuPopover>
                <Menu>
                  <MenuItem
                    className="text-xs"
                    onAction={() => setCreateKeyMessageModalOpen(true)}
                  >
                    <PlusIcon className="text-gray-600" />
                    New key topic
                  </MenuItem>
                  <MenuItem
                    className="text-xs"
                    onAction={() => setCreateKeyMessageGroupModalOpen(true)}
                  >
                    <FolderPlusIcon className="text-gray-600" />
                    New group
                  </MenuItem>
                </Menu>
              </MenuPopover>
            </MenuTrigger>
          }
        >
          <div>Key Topics</div>
        </AccordionTriggerWithActions>
        <AccordionContent className="pb-2">
          {isFetchingKeyMessageGroups ? (
            <ItemSkeleton />
          ) : (
            <>
              {shouldShowSearch && (
                <div className="sticky top-0 bg-white">
                  <SearchBar
                    isOpen={isExpanded}
                    searchTerm={searchTerm}
                    onSearchChange={handleSearchChange}
                    onClearSearch={handleClearSearch}
                    title=""
                  />
                </div>
              )}

              <div className="space-y-1">
                {feedId &&
                  filteredGroups.slice(0, visibleCount).map((group) => {
                    const isGroupSelected = selectedGroups.has(group.id);
                    const isGroupExpanded = expandedGroups.has(group.id);

                    return (
                      <KeyMessageGroupItem
                        key={group.id}
                        group={group}
                        isGroupSelected={isGroupSelected}
                        isGroupExpanded={isGroupExpanded}
                        selectedKeyMessageIds={selectedKeyMessageIds}
                        keyMessageGroups={keyMessageGroups}
                        feedId={feedId}
                        onToggleGroupSelection={toggleGroupSelection}
                        onToggleExpandGroups={toggleExpandGroups}
                        onToggleItemSelection={toggleItemSelection}
                        onCreateKeyMessage={() =>
                          setCreateKeyMessageModalOpen(true)
                        }
                        onSetSelectedGroup={setSelectedGroup}
                      />
                    );
                  })}
                {hasMoreToShow ? (
                  <ShowMoreButton
                    isExpanded={false}
                    onToggle={toggleShowMore}
                  />
                ) : canShowLess ? (
                  <ShowMoreButton isExpanded={true} onToggle={toggleShowLess} />
                ) : null}
                {filteredKeyMessages.length === 0 && searchTerm && (
                  <p className="text-[10px] text-slate-500 ml-6">
                    No key topics found
                  </p>
                )}
                {keyMessages.length === 0 &&
                  !isFetchingKeyMessageGroups &&
                  !searchTerm && (
                    <Button
                      variant="ghost"
                      size="none"
                      className="text-[10px] font-normal flex text-gray-900 pl-1.5 py-0.5 w-full justify-start"
                      onPress={() => setCreateKeyMessageModalOpen(true)}
                    >
                      <PlusIcon size={10} className="text-gray-600 mr-2" />
                      New key topic
                    </Button>
                  )}
              </div>
            </>
          )}
        </AccordionContent>
      </AccordionItem>
    </Accordion>
  );
};

export default KeyMessageFilterContainer;
